import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  CircularProgress
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import InvoiceSearch from "./InvoiceProduct/InvoiceSearch";
import InvoiceProductForm from "./InvoiceProduct/InvoiceProductForm";

const normalizeBodegas = (data) => {
  if (Array.isArray(data?.bodegas)) return data.bodegas;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
};

export default function InfoProduct({
  onProductDataChange,
  rows,
  setRows,
  descripcion,
  setDescripcion
}) {
  const searchRef = useRef();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [bodegas, setBodegas] = useState([]);
  const [selectedBodega, setSelectedBodega] = useState("");
  const [loadingBodegas, setLoadingBodegas] = useState(false);

  const safeRows = Array.isArray(rows) ? rows : [];

  const normalizeDecimalInput = (raw) => {
    let s = String(raw ?? "");
    s = s.replace(/,/g, ".");
    s = s.replace(/[^\d.]/g, "");
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
      const parts = s.split(".");
      s = `${parts[0]}.${(parts[1] || "").slice(0, 6)}`;
    }
    return s;
  };

  const normalizeIntegerInput = (raw) => {
    let s = String(raw ?? "");
    s = s.replace(/[^\d]/g, "");
    return s;
  };

  const toNum = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = Number(String(v).replace(/,/g, "."));
    return Number.isFinite(n) ? n : 0;
  };

  const round6 = (v) => Number(Number(v || 0).toFixed(6));

  const round2 = (v) => Number(Number(v || 0).toFixed(2));

  const toCents = (v) => Math.round(Number(v || 0) * 100);

  const centsToMoney = (c) => Number((Number(c || 0) / 100).toFixed(2));

  const formatMoney = (v) => Number(v || 0).toFixed(2);

  useEffect(() => {
    const cargarBodegas = async () => {
      setLoadingBodegas(true);

      try {
        const res = await fetch("/facturas/documento/detalle/?tipo_documento=FACTURA", {
          credentials: "include"
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          setBodegas([]);
          setLoadingBodegas(false);
          return;
        }

        const lista = normalizeBodegas(data).filter((b) => b.activa !== false && b.activo !== false);
        setBodegas(lista);

        const defaultId =
          data?.bodega_default?.id ||
          lista.find((b) => b.es_principal === true || b.principal === true)?.id ||
          lista[0]?.id ||
          "";

        if (defaultId) {
          setSelectedBodega(String(defaultId));
        }
      } catch {
        setBodegas([]);
      } finally {
        setLoadingBodegas(false);
      }
    };

    cargarBodegas();
  }, []);

  const calcRow = (r) => {
    const qty = r.cantidad === "" ? 0 : toNum(r.cantidad);
    const price = r.pvp === "" ? 0 : toNum(r.pvp);
    const dPct = r.descuentoPct === "" ? 0 : toNum(r.descuentoPct);
    const ivaPct = Number(r.iva_porcentaje ?? 15);

    const brutoExacto = round6(price * qty);
    const descExacto = round6((brutoExacto * dPct) / 100);
    const netoExacto = round6(brutoExacto - descExacto);

    const bruto = round2(brutoExacto);
    const descTotal = round2(descExacto);
    const neto = round2(netoExacto);

    const brutoC = toCents(bruto);
    const descC = toCents(descTotal);
    const netoC = toCents(neto);

    const ivaExacto =
      ivaPct === 15
        ? round6(netoExacto * 0.15)
        : ivaPct === 5
        ? round6(netoExacto * 0.05)
        : 0;

    const ivaValor = round2(ivaExacto);
    const ivaC = toCents(ivaValor);
    const totalLinea = centsToMoney(netoC + ivaC);
    const totalLineaC = netoC + ivaC;

    return {
      qty,
      price,
      dPct,
      ivaPct,
      brutoExacto,
      descExacto,
      netoExacto,
      ivaExacto,
      brutoC,
      descC,
      netoC,
      ivaC,
      totalLineaC,
      bruto,
      descTotal,
      neto,
      ivaValor,
      totalLinea
    };
  };

  const totals = useMemo(() => {
    return safeRows.reduce(
      (acc, r) => {
        const { brutoC, descC, netoC, ivaC, ivaPct } = calcRow(r);

        if (ivaPct === 15) {
          acc.bruto15C += brutoC;
          acc.neto15C += netoC;
          acc.iva15C += ivaC;
        } else if (ivaPct === 5) {
          acc.bruto5C += brutoC;
          acc.neto5C += netoC;
          acc.iva5C += ivaC;
        } else if (ivaPct === 999) {
          acc.brutoNoObjC += brutoC;
          acc.netoNoObjC += netoC;
        } else {
          acc.bruto0C += brutoC;
          acc.neto0C += netoC;
        }

        acc.descuentoC += descC;
        return acc;
      },
      {
        bruto15C: 0,
        bruto5C: 0,
        bruto0C: 0,
        brutoNoObjC: 0,
        neto15C: 0,
        neto5C: 0,
        neto0C: 0,
        netoNoObjC: 0,
        iva15C: 0,
        iva5C: 0,
        descuentoC: 0
      }
    );
  }, [safeRows]);

  const subtotalSinImpuestosC =
    totals.neto15C + totals.neto5C + totals.neto0C + totals.netoNoObjC;

  const iva15 = centsToMoney(totals.iva15C);
  const iva5 = centsToMoney(totals.iva5C);
  const total = centsToMoney(subtotalSinImpuestosC + totals.iva15C + totals.iva5C);

  const productosPayload = useMemo(() => {
    return safeRows.map((r) => {
      const calc = calcRow(r);
      const bodegaId = selectedBodega || "";

      return {
        product_id: r.product_id ?? null,
        variant_id: r.variant_id ?? null,
        code: r.code || "",
        sku: r.sku || "",
        variant_type: r.variant_type || "",
        value: r.value || "",
        codigo_principal: r.codigo_principal || "",
        codigo_auxiliar: r.codigo_auxiliar || "",
        descripcion: r.descripcion || "",
        detalles: r.detalles || "",
        cantidad: r.cantidad ?? "",
        pvp: r.pvp ?? "",
        iva_porcentaje: r.iva_porcentaje ?? 0,
        descuentoPct: r.descuentoPct ?? "",
        precio_unitario: r.pvp ?? "",
        precio_total_sin_impuesto: formatMoney(calc.neto),
        descuento_valor: formatMoney(calc.descTotal),
        base_imponible: formatMoney(calc.neto),
        iva_valor: formatMoney(calc.ivaValor),
        total_linea: formatMoney(calc.totalLinea),
        bodega_id: bodegaId,
        bodega: bodegaId
      };
    });
  }, [safeRows, selectedBodega]);

  useEffect(() => {
    onProductDataChange?.({
      productos: productosPayload,
      productos_raw: safeRows,
      subtotal15: centsToMoney(totals.neto15C),
      subtotal5: centsToMoney(totals.neto5C),
      subtotal0: centsToMoney(totals.neto0C),
      subtotalNoObj: centsToMoney(totals.netoNoObjC),
      subtotal15_bruto: centsToMoney(totals.bruto15C),
      subtotal5_bruto: centsToMoney(totals.bruto5C),
      subtotal0_bruto: centsToMoney(totals.bruto0C),
      subtotalNoObj_bruto: centsToMoney(totals.brutoNoObjC),
      subtotal_sin_impuestos: centsToMoney(subtotalSinImpuestosC),
      descuento_total: centsToMoney(totals.descuentoC),
      iva15,
      iva5,
      ice: 0,
      total,
      descripcion: descripcion || "",
      descuentos_ya_aplicados_en_subtotales: true,
      bodega_id: selectedBodega || "",
      bodega: selectedBodega || ""
    });
  }, [
    productosPayload,
    safeRows,
    totals,
    subtotalSinImpuestosC,
    iva15,
    iva5,
    total,
    descripcion,
    selectedBodega,
    onProductDataChange
  ]);

  const handleBodegaChange = (value) => {
    const bodegaValue = String(value || "");
    setSelectedBodega(bodegaValue);

    setRows(
      safeRows.map((r) => ({
        ...r,
        bodega_id: bodegaValue,
        bodega: bodegaValue
      }))
    );
  };

  const handleAdd = ({
    pvp,
    iva_porcentaje,
    detalles,
    descuentoPct,
    codigo_auxiliar,
    stock
  }) => {
    if (!selectedBodega) {
      setErrorMessage("Debe seleccionar una bodega.");
      return;
    }

    if (!selectedProduct) {
      setErrorMessage("Debe seleccionar un producto.");
      return;
    }

    const skuFinal = selectedProduct.sku || "";
    const codeFinal = selectedProduct.code || "";
    const codePrincipal = skuFinal ? `${codeFinal}-${skuFinal}` : codeFinal;
    const codeAux = codigo_auxiliar || selectedProduct.codigo_auxiliar || "";
    const baseName = selectedProduct.name || "";
    const pvpNorm = normalizeDecimalInput(pvp);
    const descNorm = normalizeDecimalInput(descuentoPct);

    setRows([
      ...safeRows,
      {
        product_id: selectedProduct.product_id ?? selectedProduct.id ?? null,
        variant_id: selectedProduct.variant_id ?? null,
        code: selectedProduct.code || "",
        sku: selectedProduct.sku || "",
        variant_type: selectedProduct.variant_type || "",
        value: selectedProduct.value || "",
        codigo_principal: codePrincipal,
        codigo_auxiliar: codeAux,
        cantidad: "1",
        descripcion: baseName,
        detalles: detalles || "",
        pvp: pvpNorm,
        iva_porcentaje: Number(iva_porcentaje),
        descuentoPct: descNorm === "" ? "0" : String(Number(descNorm)),
        stock: stock ?? selectedProduct.stock ?? 0,
        bodega_id: selectedBodega || "",
        bodega: selectedBodega || ""
      }
    ]);

    setSelectedProduct(null);
    searchRef.current?.clear();
    setErrorMessage("");
  };

  const handleDelete = (idx) => {
    setRows(safeRows.filter((_, i) => i !== idx));
  };

  const handleFieldChange = (idx, field, value) => {
    setRows(
      safeRows.map((r, i) => {
        if (i !== idx) return r;

        if (field === "cantidad") {
          const v = normalizeIntegerInput(value);
          return { ...r, cantidad: v };
        }

        if (field === "descuentoPct") {
          const v = normalizeDecimalInput(value);
          if (v === "" || /^\d*\.?\d*$/.test(v)) return { ...r, descuentoPct: v };
          return r;
        }

        if (field === "pvp") {
          const v = normalizeDecimalInput(value);
          if (v === "" || /^\d*\.?\d*$/.test(v)) return { ...r, pvp: v };
          return r;
        }

        if (field === "iva_porcentaje") return { ...r, iva_porcentaje: Number(value) };

        return { ...r, [field]: value };
      })
    );
  };

  const renderDescripcion = (r) => {
    const desc = (r.descripcion || "").trim();
    const det = (r.detalles || "").trim();
    if (!det) return desc;
    return `${desc}\n- ${det}`;
  };

  const renderCodigo = (r) => {
    const principal = String(r.codigo_principal || "").trim();
    const auxiliar = String(r.codigo_auxiliar || "").trim();
    return auxiliar ? `${principal} / ${auxiliar}` : principal;
  };

  return (
    <Box sx={{ width: "100%" }}>
      {errorMessage && (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}

      <Grid container spacing={2} alignItems="flex-start">
        <InvoiceSearch
          ref={searchRef}
          onSelect={setSelectedProduct}
          bodegaId={selectedBodega}
          disabled={!selectedBodega}
        />

        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel size="small">Seleccionar Bodega</InputLabel>
            <Select
              size="small"
              value={selectedBodega}
              label="Bodega de salida"
              onChange={(e) => handleBodegaChange(e.target.value)}
              disabled={loadingBodegas || bodegas.length === 0}
              sx={{
                height: 40,
                borderRadius: 3,
                bgcolor: "#fff",
                "& .MuiSelect-select": {
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              {bodegas.map((b) => (
                <MenuItem key={b.id} value={String(b.id)}>
                  {b.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loadingBodegas && (
            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Cargando bodegas...
              </Typography>
            </Box>
          )}
        </Grid>

        <InvoiceProductForm
          producto={selectedProduct}
          onAdd={handleAdd}
          bodegaId={selectedBodega}
        />
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1565C0" }}>
              <TableCell sx={{ color: "#fff" }}>Código / Auxiliar</TableCell>
              <TableCell sx={{ color: "#fff" }}>Cant.</TableCell>
              <TableCell sx={{ color: "#fff" }}>Descripción</TableCell>
              <TableCell sx={{ color: "#fff" }}>Precio U.</TableCell>
              <TableCell sx={{ color: "#fff" }}>IVA (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. ($)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Subtotal</TableCell>
              <TableCell sx={{ color: "#fff" }}>Eliminar</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {safeRows.map((r, i) => {
              const { descTotal, neto } = calcRow(r);

              return (
                <TableRow key={i}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {renderCodigo(r)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.cantidad}
                      onChange={(e) => handleFieldChange(i, "cantidad", e.target.value)}
                      sx={{ width: 60 }}
                      inputProps={{ inputMode: "numeric" }}
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 320 }}>
                    <TextField
                      value={renderDescripcion(r)}
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={8}
                      InputProps={{ readOnly: true }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.pvp}
                      onChange={(e) => handleFieldChange(i, "pvp", e.target.value)}
                      type="text"
                      sx={{ width: 115 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>
                    <Select
                      value={r.iva_porcentaje}
                      onChange={(e) => handleFieldChange(i, "iva_porcentaje", e.target.value)}
                      sx={{ width: 100 }}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={15}>15%</MenuItem>
                      <MenuItem value={999}>No objeto</MenuItem>
                    </Select>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.descuentoPct}
                      onChange={(e) => handleFieldChange(i, "descuentoPct", e.target.value)}
                      sx={{ width: 70 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>${formatMoney(descTotal || 0)}</TableCell>
                  <TableCell>${formatMoney(neto || 0)}</TableCell>

                  <TableCell>
                    <IconButton onClick={() => handleDelete(i)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}